// F4 fasting — a timer, not a coach. Elapsed time is stated plainly; ending
// early is one tap with no confirmation and no guilt copy; the streak is a
// neutral fact. Nothing here ever suggests fasting longer.
import { useEffect, useState } from "react";
import { Timer } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { hapticTap, hapticSuccess } from "../lib/haptics";
import { todayLocal } from "../lib/date";
import { Paywall } from "./Paywall";
import { useEntitlement } from "../lib/useEntitlement";
import type { Fast, FastsResponse } from "@shared/types";

const PROTOCOLS: Array<{ id: Fast["protocol"]; premium: boolean }> = [
  { id: "16:8", premium: false },
  { id: "18:6", premium: true },
  { id: "20:4", premium: true },
  { id: "5:2", premium: true },
];

function fmtElapsed(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

export function FastingCard() {
  const t = useT();
  const toast = useToast();
  const [data, setData] = useState<FastsResponse | null>(null);
  const [protocol, setProtocol] = useState<Fast["protocol"]>("16:8");
  const [busy, setBusy] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [, tick] = useState(0);
  const { billingEnabled } = useEntitlement();

  async function load() {
    try { setData(await api.fasts(todayLocal())); }
    catch { /* card stays in its last state offline */ }
  }
  useEffect(() => { void load(); }, []);

  // Re-render the elapsed display once a minute while a fast runs.
  useEffect(() => {
    if (!data?.current) return;
    const id = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [data?.current]);

  async function startFast() {
    setBusy(true);
    try {
      await api.startFast(protocol);
      hapticSuccess();
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.message === "premium_required") setShowPaywall(true);
      else toast.error(t("common.failed"));
    } finally {
      setBusy(false);
    }
  }

  async function endFast() {
    setBusy(true);
    try {
      await api.endFast();
      hapticTap();
      toast.success(t("fast.ended_toast"));
      await load();
    } catch {
      toast.error(t("common.failed"));
    } finally {
      setBusy(false);
    }
  }

  if (data === null) return null;
  const cur = data.current;
  const elapsed = cur ? Math.max(0, Date.now() / 1000 - cur.started_at) : 0;
  const targetSec = cur ? cur.target_hours * 3600 : 0;
  const pct = cur ? Math.min(1, elapsed / targetSec) : 0;
  const eatingWindow = cur && elapsed >= targetSec;

  return (
    <section className="glass rounded-2xl p-3.5">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="w-9 h-9 rounded-xl bg-surface-strong flex items-center justify-center">
          <Timer size={18} />
        </span>
        <h3 className="text-sm font-semibold flex-1">{t("fast.title")}</h3>
        {data.streak_days > 0 && (
          <span className="text-sm text-fg-muted tabular-nums">
            {t("fast.streak", { n: String(data.streak_days) })}
          </span>
        )}
      </div>

      {cur ? (
        <div className="mt-3 space-y-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-semibold tabular-nums">{fmtElapsed(elapsed)}</span>
            <span className="text-sm text-fg-muted">
              {eatingWindow
                ? t("fast.window_open")
                : t("fast.of_target", { h: String(cur.target_hours) })}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-surface-strong overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-500 bg-brand"
              style={{ width: `${pct * 100}%` }}
            />
          </div>
          <button
            onClick={endFast}
            disabled={busy}
            className="w-full min-h-[44px] rounded-xl bg-surface text-sm font-medium press active:bg-surface-strong disabled:opacity-50"
          >
            {t("fast.end")}
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-2.5">
          <div className="flex gap-1.5">
            {PROTOCOLS.map((p) => (
              <button
                key={p.id}
                onClick={() => { hapticTap(); setProtocol(p.id); }}
                aria-pressed={protocol === p.id}
                className={`flex-1 min-h-[44px] rounded-xl text-sm font-medium transition-colors ${
                  protocol === p.id ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary active:bg-surface-strong"
                }`}
              >
                {p.id}
                {p.premium && billingEnabled && <span aria-hidden="true" className="ml-0.5 text-sm">✦</span>}
              </button>
            ))}
          </div>
          <button
            onClick={startFast}
            disabled={busy}
            className="w-full min-h-[44px] rounded-xl bg-surface text-sm font-medium press active:bg-surface-strong disabled:opacity-50"
          >
            {t("fast.start")}
          </button>
        </div>
      )}
      {showPaywall && <Paywall trigger="feature" onClose={() => setShowPaywall(false)} />}
    </section>
  );
}
